/**
 * Modelo de estado del formulario de precios y transformaciones
 * GET (`PreciosConfig`) <-> estado del form <-> body del PUT (`UpdatePreciosBody`).
 *
 * Aislado de React a propósito: es lógica pura (fácil de testear) y concentra las
 * **trampas** del contrato (`progress/explore_precios_api.md` §6 y
 * `progress/explore_precios_ssr.md` §2c):
 *  - El estado del form guarda los números como **strings** (lo que el usuario
 *    teclea), igual que el `OrderForm` con montos; se parsean al construir el body.
 *  - `precios_dobles` y `combinada_medidas` son dinámicos (add/remove de filas).
 *  - Las **6 tablas de la Sección 3** solo editan celdas (sin add/remove, como el
 *    SSR); se reagrupan por calibre y **se ordenan por `hasta` ascendente** antes
 *    de enviar (el motor de cálculo asume tramos ordenados).
 *  - Al construir el body se **descartan filas vacías/no positivas** (replicando
 *    los `collect*` del SSR) y nunca se manda `NaN`/vacío (el backend revienta con
 *    500). El `success` del GET NO viaja en el body.
 */
import type {
  PrecioPar,
  PreciosConfig,
  TablaPorCalibre,
  UpdatePreciosBody,
} from "./precios.types";
import {
  TABLAS_CALIBRE,
  type PrecioEscalarKey,
  type TablaCalibreKey,
} from "./precios.constants";

/** Una fila editable de doblez por calibre (Sección 2). */
export interface DoblesRow {
  calibre: string;
  valor: string;
}

/** Una fila editable de medida combinada (Sección 2): par `[ancho, alto]`. */
export interface CombinadaRow {
  ancho: string;
  alto: string;
}

/** Una fila editable de una tabla por tramo: par `[hasta, precio]`. */
export interface TablaRow {
  hasta: string;
  precio: string;
}

/** Bloque de filas de una tabla por calibre: `{ "20": [...filas], "22": [...] }`. */
export type TablaCalibreState = Record<string, TablaRow[]>;

/**
 * Estado completo del formulario de precios. Los escalares y celdas son strings
 * (entrada controlada); las estructuras dinámicas son arrays con índice estable
 * de render.
 */
export interface PreciosFormState {
  escalares: Record<PrecioEscalarKey, string>;
  dobles: DoblesRow[];
  combinada: CombinadaRow[];
  tablasCalibre: Record<TablaCalibreKey, TablaCalibreState>;
  tuboRemarco: TablaRow[];
}

/** Claves escalares en el orden en que se inicializan (todas las numéricas). */
const ESCALAR_KEYS: PrecioEscalarKey[] = [
  "precio_kg_venta",
  "costo_kg_compra",
  "factor_utilidad",
  "iva",
  "perfiles",
  "cantidad",
  "dobles_interno_general",
  "dobles_refuerzo",
  "dobles_por_tubo",
  "dobles_division",
];

/**
 * Convierte un número del backend a string para el input. Se evita notación
 * científica/redondeos sorpresa usando la representación natural de `String`
 * (`5685` -> "5685", `0.9` -> "0.9", `1.19` -> "1.19").
 */
function numToInput(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "";
  }
  return String(value);
}

/** Pasa una tabla por calibre del backend a filas editables (string). */
function tablaToRows(tabla: TablaPorCalibre | undefined): TablaCalibreState {
  const state: TablaCalibreState = {};
  if (!tabla) {
    return state;
  }
  for (const [calibre, pares] of Object.entries(tabla)) {
    state[calibre] = (pares ?? []).map(([hasta, precio]) => ({
      hasta: numToInput(hasta),
      precio: numToInput(precio),
    }));
  }
  return state;
}

/**
 * Construye el estado inicial del formulario desde la respuesta del GET.
 * Las filas de `precios_dobles` se ordenan por calibre numérico ascendente para
 * una presentación estable (16, 18, 20, 22…).
 */
export function configToFormState(config: PreciosConfig): PreciosFormState {
  const escalares = {} as Record<PrecioEscalarKey, string>;
  for (const key of ESCALAR_KEYS) {
    escalares[key] = numToInput(config[key]);
  }

  const dobles: DoblesRow[] = Object.entries(config.precios_dobles ?? {})
    .map(([calibre, valor]) => ({ calibre, valor: numToInput(valor) }))
    .sort((a, b) => Number(a.calibre) - Number(b.calibre));

  const combinada: CombinadaRow[] = (config.combinada_medidas ?? []).map(
    ([ancho, alto]) => ({ ancho: numToInput(ancho), alto: numToInput(alto) }),
  );

  const tablasCalibre = {} as Record<TablaCalibreKey, TablaCalibreState>;
  for (const { key } of TABLAS_CALIBRE) {
    tablasCalibre[key] = tablaToRows(config[key]);
  }

  const tuboRemarco: TablaRow[] = (config.precios_tubo_remarco ?? []).map(
    ([hasta, precio]) => ({ hasta: numToInput(hasta), precio: numToInput(precio) }),
  );

  return { escalares, dobles, combinada, tablasCalibre, tuboRemarco };
}

/**
 * Siguiente calibre par a proponer al añadir una fila de doblez (réplica del
 * contador `nextCalibre` del SSR: `max(calibres) + 2`, o 24 si no hay ninguno;
 * los calibres del dominio son pares). Devuelve string (el calibre es clave).
 */
export function nextCalibre(dobles: DoblesRow[]): string {
  const numeros = dobles
    .map((row) => Number(row.calibre))
    .filter((n) => Number.isFinite(n));
  if (numeros.length === 0) {
    return "24";
  }
  return String(Math.max(...numeros) + 2);
}

/** Parsea un string a número finito, o `null` si está vacío / no es número. */
function parseNum(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * Recolecta las filas de una tabla (calibre) descartando inválidas y ordenando
 * por `hasta` ascendente (réplica de `collectTablas` del SSR): incluye una fila
 * solo si `hasta > 0` y `precio >= 0`. Devuelve `[hasta, precio]` numéricos.
 */
function collectTablaRows(rows: TablaRow[]): PrecioPar[] {
  const pares: PrecioPar[] = [];
  for (const row of rows) {
    const hasta = parseNum(row.hasta);
    const precio = parseNum(row.precio);
    if (hasta === null || precio === null) {
      continue;
    }
    if (hasta <= 0 || precio < 0) {
      continue;
    }
    pares.push([hasta, precio]);
  }
  pares.sort((a, b) => a[0] - b[0]);
  return pares;
}

/**
 * Construye el cuerpo del PUT desde el estado del formulario, replicando los
 * descartes/ordenamientos del SSR y garantizando que NUNCA se envíe `NaN`/vacío:
 *  - Escalares: número finito; un campo vacío/no numérico cae a `0` (no se omite,
 *    para que el campo quede definido y consistente; la validación previa ya
 *    impide enviar formularios con escalares inválidos).
 *  - `precios_dobles`: solo calibres con `valor > 0` (réplica `collectPreciosDobles`).
 *  - `combinada_medidas`: solo filas con `ancho > 0 && alto > 0`.
 *  - Tablas: `collectTablaRows` (descarte + orden por `hasta`).
 *
 * NO incluye `success` ni claves ajenas a las 18 del form.
 */
export function formStateToBody(state: PreciosFormState): UpdatePreciosBody {
  const escalares = {} as Record<PrecioEscalarKey, number>;
  for (const key of ESCALAR_KEYS) {
    const value = parseNum(state.escalares[key] ?? "");
    escalares[key] = value ?? 0;
  }

  const precios_dobles: Record<string, number> = {};
  for (const row of state.dobles) {
    const calibre = row.calibre.trim();
    const valor = parseNum(row.valor);
    if (calibre === "" || valor === null || valor <= 0) {
      continue;
    }
    precios_dobles[calibre] = valor;
  }

  const combinada_medidas: PrecioPar[] = [];
  for (const row of state.combinada) {
    const ancho = parseNum(row.ancho);
    const alto = parseNum(row.alto);
    if (ancho === null || alto === null || ancho <= 0 || alto <= 0) {
      continue;
    }
    combinada_medidas.push([ancho, alto]);
  }

  const tablas = {} as Record<TablaCalibreKey, TablaPorCalibre>;
  for (const { key } of TABLAS_CALIBRE) {
    const tabla: TablaPorCalibre = {};
    for (const [calibre, rows] of Object.entries(state.tablasCalibre[key] ?? {})) {
      const pares = collectTablaRows(rows);
      if (pares.length > 0) {
        tabla[calibre] = pares;
      }
    }
    tablas[key] = tabla;
  }

  return {
    precio_kg_venta: escalares.precio_kg_venta,
    costo_kg_compra: escalares.costo_kg_compra,
    factor_utilidad: escalares.factor_utilidad,
    iva: escalares.iva,
    perfiles: escalares.perfiles,
    cantidad: escalares.cantidad,
    dobles_interno_general: escalares.dobles_interno_general,
    dobles_refuerzo: escalares.dobles_refuerzo,
    dobles_por_tubo: escalares.dobles_por_tubo,
    dobles_division: escalares.dobles_division,
    precios_dobles,
    combinada_medidas,
    precios_cabezal: tablas.precios_cabezal,
    precios_jamba: tablas.precios_jamba,
    precios_refuerzo: tablas.precios_refuerzo,
    precios_division_6: tablas.precios_division_6,
    precios_division_8: tablas.precios_division_8,
    precios_tubo_remarco: collectTablaRows(state.tuboRemarco),
  };
}
